
import { Component, Vue } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import TicketRepositoryInterface, { TicketInterface } from '@/services/QC-V2/ticket/interfaces';
import { ProfileMapInterface } from '@/services/QC-V2/chat/interfaces';
import { TagInterface, TagRepositoryInterface } from '@/services/QC-V2/tag/interfaces';
import { ScoreTypeInterface, ScoreTypeRepositoryInterface } from '@/services/QC-V2/score-type/interfaces';
import { ScoreGradeInterface, ScoreGradeRepositoryInterface } from '@/services/QC-V2/score-grade/interfaces';
import { UserBaseInfoInterface, UserRepoInterface } from '@/services/Users/Interfaces';
import { ProfileRepositoryInterface } from '@/services/Users-profile/interfaces';
import ViewTicket from '@/components/qa/ticket/ViewTicket.vue';

interface ProfilesSelectInterface {
  id: string,
  name: string,
}

@Component({
  mixins: [
    Themeable,
  ],
  components: {
    ViewTicket,
  },
  inject: [
    'ticketRepositoryV2',
    'userRepository',
    'profileRepository',
    'scoreTypeRepositoryV2',
    'scoreGradeRepositoryV2',
    'tagRepositoryV2',
  ],
})
export default class Ticket extends Vue {
  public $refs!: {
    ticketsCol: HTMLFormElement,
    row: HTMLFormElement,
  };

  private loading: boolean = true;

  private ticketId: number = -1;
  private ticket: TicketInterface = {} as TicketInterface;

  private profiles: ProfilesSelectInterface[] = [];
  private users: UserBaseInfoInterface[] = [];
  private profilesMap: ProfileMapInterface = {};

  private tags: TagInterface[] = [];
  private scoreTypes: ScoreTypeInterface[] = [];
  private scoreGrades: ScoreGradeInterface[] = [];

  private ticketRepositoryV2!: TicketRepositoryInterface;
  private userRepository!: UserRepoInterface;
  private profileRepository!: ProfileRepositoryInterface;
  private readonly scoreTypeRepositoryV2!: ScoreTypeRepositoryInterface;
  private readonly scoreGradeRepositoryV2!: ScoreGradeRepositoryInterface;
  private readonly tagRepositoryV2!: TagRepositoryInterface;

  private async created(): Promise<void> {
    if (!this.$route.params.ticketId) {
      this.$errorDialog.show('Ticket ID not provided!');
    }

    this.ticketId = Number(this.$route.params.ticketId);
    try {
      await this.fetchTags();
      await this.fetchScores();
      await this.initialize();
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private async initialize(): Promise<void> {
    try {
      await this.getTicket(this.ticketId);
      this.loading = false;
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private async fetchTags(): Promise<void> {
    this.tags = await this.tagRepositoryV2.getActiveTags();
  }

  private async fetchScores(): Promise<void> {
    this.scoreTypes = await this.scoreTypeRepositoryV2.getAll();
    this.scoreGrades = await this.scoreGradeRepositoryV2.getAll();
  }

  private async getTicket(id: number): Promise<void> {
    this.loading = true;
    const ticket = await this.ticketRepositoryV2.getTicketById(id);
    await this.fetchTicketProfilesData(ticket);
    this.ticket = ticket;
    this.loading = false;
  }

  private async fetchTicketProfilesData(ticket: TicketInterface): Promise<void> {
    this.profiles = [];

    const idsForReview = await this.extractUserIdsForReview(ticket);
    const idsFromTicketData = this.extractUserIdsFromTicketData(ticket);
    const existingReviewIds = this.extractExistingReviewUsersIds(ticket);

    const profiles = await this.profileRepository
      .getBaseProfileDataByUsersIds(
        [...idsFromTicketData, ...idsForReview, ...existingReviewIds]
          .filter((id, index, self) => self.indexOf(id) === index),
      );

    this.profiles.push({
      id: 'ticket',
      name: 'Ticket Overall',
    });

    const profilesForReview = profiles.filter((profile) => idsForReview.includes(profile.userId))
      .map((profile) => ({
        id: profile.userId,
        name: `${profile.firstName} ${profile.lastName}`,
      }));

    this.profiles.push(...profilesForReview);

    const [firstName, lastName] = this.$store.getters.name.split(' ');
    this.profilesMap[this.$store.getters.id] = {
      userId: this.$store.getters.id,
      profileId: '',
      firstName,
      lastName,
      fullName: '',
    };

    profiles.forEach((profile) => {
      this.profilesMap[profile.userId] = profile;
    });
  }

  private extractExistingReviewUsersIds(ticket: TicketInterface): string[] {
    if (!ticket.reviews.length) {
      return [];
    }

    const ids: string[] = [];
    ticket.reviews.forEach((review) => {
      if (!ids.includes(review.reviewerId)) {
        ids.push(review.reviewerId);
      }

      if (!ids.includes(review.reviewedId)) {
        ids.push(review.reviewerId);
      }

      if (!review.comments.length) {
        return;
      }

      review.comments.forEach((comment) => {
        if (!ids.includes(comment.author)) {
          ids.push(comment.author);
        }
      });
    });

    return ids;
  }


  private extractUserIdsFromTicketData(ticket: TicketInterface): string[] {
    const ids: string[] = [];
    ticket.comments.forEach((comment) => {
      if (!ids.includes(comment.author)) {
        ids.push(comment.author);
      }
    });

    ticket.notes.forEach((note) => {
      if (!ids.includes(note.author)) {
        ids.push(note.author);
      }
    });

    ticket.replies.forEach((reply) => {
      if (!reply.comments.length) {
        return;
      }

      reply.comments.forEach((comment) => {
        if (!ids.includes(comment.author)) {
          ids.push(comment.author);
        }
      });
    });

    ticket.reviews.forEach((review) => {
      if (!ids.includes(review.reviewerId)) {
        ids.push(review.reviewerId);
      }

      if (!ids.includes(review.reviewedId)) {
        ids.push(review.reviewedId);
      }
    });

    return ids;
  }

  private async extractUserIdsForReview(ticket: TicketInterface): Promise<string[]> {
    const ids: string[] = [];
    const emails = ticket.agents.map((agent) => agent.email);

    const users = await this.userRepository.getUsersByEmails(emails);
    this.users = users;

    users.forEach((user) => {
      if (!ids.includes(user.id)) {
        ids.push(user.id);
      }
    });

    return ids;
  }
}
